import { PartialMessage } from '@bufbuild/protobuf';
import {
  ActionsPopup,
  CustomUploadResponse,
  DragAndDropFiles,
  IStyles,
  PrimaryButton,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getImageType,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  uploadFile,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { FileUploadPurpose } from '@protos/learning_management/lms.common.apis_pb';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { ChangeEvent, DragEvent, useEffect, useState } from 'react';

//~ Mirrors getSignedUrlForFileUpload's project-submission limits: mp4 gets
//~ 200 MB, anything else 50 MB
const MAX_VIDEO_SIZE_IN_MB = 200;
const MAX_PHOTO_SIZE_IN_MB = 50;

//~ What the file picker offers each field - the formats getFileType maps to
//~ FILE_TYPE_IMAGE and FILE_TYPE_VIDEO, so anything pickable can upload
const PHOTO_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';
const VIDEO_ACCEPT = 'video/mp4,.mp4';

const styles: IStyles = {
  modal: {
    '& .MuiDialog-paper': {
      width: { xs: '92vw', md: '70vw', lg: pxTovW(760) },
      maxWidth: 'none',
      m: { xs: '4vw', md: pxToRem(32) },
      borderRadius: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
      p: { xs: pxToRem(20), md: pxToRem(24), lg: pxTovW(30) },
      boxSizing: 'border-box',
    },
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
  },
  buttonRow: {
    display: 'flex',
    gap: { xs: pxToRem(12), md: pxToRem(16), lg: pxTovW(16) },
    justifyContent: 'flex-end',
  },
};

interface IProps {
  open: boolean;
  submitting: boolean;
  studentId?: bigint;
  assessmentId?: bigint;
  sessionId?: bigint;
  sectionId?: bigint;
  subjectId?: number;
  handleClose: () => void;
  handleSubmit: (userUploads: PartialMessage<CloudUpload>[]) => void;
}

/**
 * Collects the evidence a student attaches when submitting a project - a photo
 * and a short video of the working build, both optional.
 *
 * Files are pushed to cloud storage as soon as they are picked, so Submit only
 * has to hand the parent the CloudUpload descriptors of what landed. A failed
 * upload is shown on the field and simply left out of that list rather than
 * blocking the submit - the evidence is optional, the submission is not.
 */
export const ProjectEvidencePopup = (props: IProps) => {
  const {
    open,
    submitting,
    studentId,
    assessmentId,
    sessionId,
    sectionId,
    subjectId,
    handleClose,
    handleSubmit,
  } = props;

  const { LmsCommonAPIServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const [photoUpload, setPhotoUpload] = useState<CustomUploadResponse[]>([]);
  const [videoUpload, setVideoUpload] = useState<CustomUploadResponse[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    //~ Each opening starts a fresh submission - a file picked and then
    //~ cancelled must not ride along on the next attempt.
    if (open) {
      setPhotoUpload([]);
      setVideoUpload([]);
      setConfirmOpen(false);
    }
  }, [open]);

  const isUploading = [...photoUpload, ...videoUpload].some(
    (upload) => upload.fileUploadStatus === 'pending'
  );

  const uploadEvidence = async (
    files: FileList | null,
    expectedFileType: FileEnum,
    setUpload: (uploads: CustomUploadResponse[]) => void
  ) => {
    //~ One photo and one video only - a fresh pick replaces the previous file.
    const file = files?.[0];
    if (!file) {
      return;
    }

    const fileType = getFileType(file.type);
    const upload: CustomUploadResponse = {
      uploadFileData: {
        fileName: file.name,
        personId: studentId,
        fileType: fileType,
        fileExtensionType: getFileExtensionType(file.type),
        fileSizeInMb: getFileSizeInMB(file.size),
        isUploaded: false,
        isDeleted: false,
      },
      fileUploadStatus: 'pending',
      fileSize: file.size,
    };

    if (fileType !== expectedFileType) {
      setUpload([{ ...upload, fileUploadStatus: 'Invalid Type' }]);
      return;
    }
    const maxFileSizeInMb =
      fileType === FileEnum.FILE_TYPE_VIDEO
        ? MAX_VIDEO_SIZE_IN_MB
        : MAX_PHOTO_SIZE_IN_MB;
    if (getFileSizeInMB(file.size) > maxFileSizeInMb) {
      setUpload([{ ...upload, fileUploadStatus: 'Size Exceeded' }]);
      return;
    }
    setUpload([upload]);

    try {
      const signedUrlResponse =
        await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
          {
            personId: studentId,
            homeworkId: assessmentId,
            // The upload API still takes an int32 session id.
            hwSessionId: sessionId !== undefined ? Number(sessionId) : undefined,
            filesInfo: [
              {
                originalFilePath: file.name,
                mimeType: getImageType(file.type),
                size: getFileSizeInMB(file.size),
              },
            ],
            personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
            fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_FOR_PROJECT_SUBMISSION,
            sectionId: sectionId,
            subjectId: subjectId,
          }
        );

      const signedUrl = signedUrlResponse?.response?.[0]?.signedUrl
        ? getMediaBasePath(signedUrlResponse.response[0].signedUrl)
        : undefined;
      if (!signedUrl) {
        setUpload([{ ...upload, fileUploadStatus: 'Upload Error' }]);
        return;
      }

      const uploadStatus = await uploadFile({ file, signedUrl });
      setUpload([
        {
          ...upload,
          uploadFileData: {
            ...upload.uploadFileData,
            signedUrl: signedUrl,
            isUploaded: uploadStatus.success,
          },
          fileUploadStatus: uploadStatus.success ? 'success' : 'Upload Error',
        },
      ]);
    } catch (error) {
      console.error('Error uploading project evidence', error);
      setUpload([{ ...upload, fileUploadStatus: 'Upload Error' }]);
    }
  };

  //~ Submitting freezes the project - the editor turns read-only from here on -
  //~ so the student gets one last chance to back out.
  const submitClickHandler = () => {
    setConfirmOpen(true);
  };

  const confirmedSubmitHandler = () => {
    setConfirmOpen(false);
    const userUploads = [...photoUpload, ...videoUpload]
      .filter((upload) => upload.fileUploadStatus === 'success')
      .map((upload) => upload.uploadFileData as PartialMessage<CloudUpload>);
    handleSubmit(userUploads);
  };

  const evidenceField = (
    label: string,
    hint: string,
    expectedFileType: FileEnum,
    accept: string,
    uploads: CustomUploadResponse[],
    setUpload: (uploads: CustomUploadResponse[]) => void
  ) => (
    <Box sx={styles.fieldGroup}>
      <Box>
        <Typography variant="h4" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="h5" color="neutral.grey">
          {hint}
        </Typography>
      </Box>
      <DragAndDropFiles
        accept={accept}
        userUploads={uploads}
        rootStyle={{ maxWidth: 'none', borderRadius: pxToRem(10) }}
        handleDropFunction={(event: DragEvent<HTMLDivElement>) =>
          uploadEvidence(event.dataTransfer.files, expectedFileType, setUpload)
        }
        handleChangeFunction={async (event: ChangeEvent<HTMLInputElement>) => {
          await uploadEvidence(event.target.files, expectedFileType, setUpload);
          //~ Reset so re-picking the same file still fires onChange
          if (event.target) event.target.value = '';
        }}
        handleDeleteFunction={() => setUpload([])}
      />
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.modal}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: pxToRem(20), md: pxToRem(21), lg: pxTovW(24) },
        }}
      >
        <Typography variant="h2" fontWeight="bold">
          Project Evidence
        </Typography>

        {evidenceField(
          'Photo of the working project (optional)',
          `Only JPG or PNG images up to ${MAX_PHOTO_SIZE_IN_MB} MB`,
          FileEnum.FILE_TYPE_IMAGE,
          PHOTO_ACCEPT,
          photoUpload,
          setPhotoUpload
        )}

        {evidenceField(
          'Short video of the working project (optional)',
          `Only MP4 videos up to ${MAX_VIDEO_SIZE_IN_MB} MB`,
          FileEnum.FILE_TYPE_VIDEO,
          VIDEO_ACCEPT,
          videoUpload,
          setVideoUpload
        )}

        <Box sx={styles.buttonRow}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={submitting}
            sx={{ width: { xs: '50%', md: pxToRem(160), lg: pxTovW(200) } }}
          >
            CANCEL
          </Button>
          <PrimaryButton
            onClick={submitClickHandler}
            disabled={submitting || isUploading}
            sx={{ width: { xs: '50%', md: pxToRem(160), lg: pxTovW(200) } }}
          >
            SUBMIT
          </PrimaryButton>
        </Box>
      </Box>

      <ActionsPopup
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        closeIcon
        fontSmall
        iconName="search"
        popupText="Once submitted, you will not be able to change your code."
        splitText="Do you wish to proceed?"
        popupTextColor="primary.main"
        splitTextColor="black"
        yesText="SUBMIT"
        noText="GO BACK"
        yesClickHandler={confirmedSubmitHandler}
        noClickHandler={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default ProjectEvidencePopup;
